import React, { useEffect, useState } from 'react';
import { useSlideGenerator } from '../hooks/useSlideGenerator';
import { useSlideController } from '../hooks/useSlideController';
import { useAuth } from '../AuthContext';

import SlidePlayer from './SlidePlayer';
import SlideThumbnailRail from './SlideThumbnailRail';
import Sidebar from './Sidebar';
import LoadingState from './LoadingState';
import PromptPage from './PromptPage';
import UpgradeModal from './UpgradeModal';
import AllExports from './AllExports';
import CurrentSlideEditor from './CurrentSlideEditor';
import { loadWebFonts } from '../canvas/fontLoader';
import './SlideGenerator.css';

function SlideGenerator() {
  const { user, userPlan } = useAuth();

  const {
    isProcessing,
    processedSlides,
    processingError,
    moderationBlock,
    slideTemplates,
    slideImages,
    isFetchingImages,
    getSlideImageUrl,
    handleReset,
    changeSlideTemplate,
    downloadSlide,
    copySlideText,
    generateFromPrompt,
    setProcessedSlides,
    planTracker,
    setSlideTemplates,
    setSlideImages,
  } = useSlideGenerator();

  const {
    fontsLoaded,
    setFontsLoaded,
    hasStarted,
    uploadMode,
    showPNGModal,
    setShowPNGModal,
    isExportingPPTX,
    showResults,
    presentationTitle,
    searchTerm,
    setSearchTerm,
    slideLayoutFilters,
    canvasRefs,
    filteredTemplatesList,
    getFilteredTemplatesForSlide,
    getLayoutFilter,
    formatTime,
    getLoadingMessage,
    getCurrentTemplate,
    handlePromptGenerate,
    handleLoadPresentation,
    handleCompleteReset,
    handleTemplateSelect,
    handleTemplateNext,
    handleTemplatePrev,
    handleDownloadAllPPTX,
    handleLayoutFilterChange,
    handleImageUpload,
    handleAnimationToggle,
  } = useSlideController({
    user,
    processedSlides,
    slideTemplates,
    slideImages,
    isProcessing,
    isFetchingImages,
    getSlideImageUrl,
    handleReset,
    changeSlideTemplate,
    generateFromPrompt,
    setProcessedSlides,
    setSlideTemplates,
    setSlideImages,
  });

  const [selectedSlides, setSelectedSlides] = useState(new Set());
  const [isExportingPNG, setIsExportingPNG] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeModalConfig, setUpgradeModalConfig] = useState({
    limitType: 'projects',
    suggestedPlan: null
  });
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

  // Reset back to slide 1 whenever a new deck comes in (new prompt run or
  // loaded presentation), so the side panel doesn't point at a stale index.
  useEffect(() => {
    setCurrentSlideIndex(0);
  }, [processedSlides.length]);

  // planTracker (including .usage) comes from useSlideGenerator() above —
  // it's the single shared instance. Don't call usePlanTracker() again
  // here, that was creating a second, out-of-sync copy of usage/plan data.

  useEffect(() => {
    loadWebFonts().then(() => {
      setFontsLoaded(true);
    });
  }, [setFontsLoaded]);

  useEffect(() => {
    if (showPNGModal) {
      const allIndices = processedSlides.map((_, i) => i);
      setSelectedSlides(new Set(allIndices));
    }
  }, [showPNGModal, processedSlides]);


 // In SlideGenerator.js - around line 145
useEffect(() => {
  // Wait for the loading state to actually render in the DOM
  if ((isProcessing || isFetchingImages) && processedSlides.length === 0 && !moderationBlock) {
    setTimeout(() => {
      const loadingElement = document.querySelector('.loading-state-inline');
      if (loadingElement) {
        loadingElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center'  // Centers it in the viewport
        });
      }
    }, 200); // Longer delay to ensure DOM is fully rendered
  }
}, [isProcessing, isFetchingImages, processedSlides.length, moderationBlock]);

  // Check if processingError is a limit error and show modal
  useEffect(() => {
  if (processingError) {
    const errorLower = processingError.toLowerCase();
    
    const isLimitError = 
      errorLower.includes('limit') ||
      errorLower.includes('exceeded') ||
      errorLower.includes('upgrade') ||
      errorLower.includes('plan');

    if (isLimitError) {
      let limitType = 'projects';
      let suggestedPlan = null;

      // Extract the suggested plan from error message if present
      if (errorLower.includes('starter')) {
        suggestedPlan = 'starter';
      } else if (errorLower.includes('creator')) {
        suggestedPlan = 'creator';
      } else if (errorLower.includes('professional')) {
        suggestedPlan = 'professional';
      }

      setUpgradeModalConfig({ limitType, suggestedPlan });
      setShowUpgradeModal(true);
    }
  }
}, [processingError]);

  const handleToggleSlide = (index) => {
    const newSelected = new Set(selectedSlides);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedSlides(newSelected);
  };

  const handleSelectAllSlides = () => {
    if (selectedSlides.size === processedSlides.length) {
      setSelectedSlides(new Set());
    } else {
      const allIndices = processedSlides.map((_, i) => i);
      setSelectedSlides(new Set(allIndices));
    }
  };

  const handleExportPNG = async () => {
    if (selectedSlides.size === 0) return;

    setIsExportingPNG(true);
    const selectedIndices = Array.from(selectedSlides).sort((a, b) => a - b);
    
    for (const slideIndex of selectedIndices) {
      await downloadSlide(processedSlides[slideIndex], slideIndex);
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    setIsExportingPNG(false);
    setShowPNGModal(false);
  };

  // Helper functions
  const getTruncatedTitle = () => {
    if (!presentationTitle) return 'New Presentation';
    
    const maxLength = 180;
    if (presentationTitle.length <= maxLength) {
      return presentationTitle;
    }
    
    return presentationTitle.substring(0, maxLength) + '...';
  };

  // Saves edited heading/subheading/bulletPoints from SlideContentList back
  // into the slide's extractedContent. SlidePlayer picks this up automatically
  // and re-renders the live preview.
  const handleUpdateSlideContent = (slideIndex, updatedContent) => {
    setProcessedSlides(prev => {
      const updated = [...prev];
      updated[slideIndex] = {
        ...updated[slideIndex],
        extractedContent: {
          ...updated[slideIndex].extractedContent,
          ...updatedContent
        }
      };
      return updated;
    });
  };

  if (!hasStarted) {
    return (
      <div className="generate-layout">
        <Sidebar onLoadPresentation={handleLoadPresentation} onNewChat={handleCompleteReset} />
        <div className="generate-content">
          <PromptPage
            onPromptGenerate={handlePromptGenerate}
            onLoadPresentation={handleLoadPresentation}
            isProcessing={isProcessing}
            isFetchingImages={isFetchingImages}
            processingError={processingError}
            moderationBlock={moderationBlock}
            userPlan={planTracker.userPlan}
          />
        </div>
      </div>
    );
  }

  const loadingInfo = getLoadingMessage();
  const showRightPanelLoading = loadingInfo !== null && processedSlides.length === 0;

  return (
    <div className="generate-layout">
      <Sidebar onLoadPresentation={handleLoadPresentation} onNewChat={handleCompleteReset} />
      <div className="generate-content">
      <div className={`generator-vertical-layout ${showResults ? 'has-results' : ''}`}>

        {/* TOP — prompt (unchanged content, just moved above everything else) */}
        <div className="prompt-top-section">
          <div className="panel-header">
            <h2 title={presentationTitle || 'New Presentation'}>
              {getTruncatedTitle()}
              <svg
                style={{ width: '16px', height: '16px', marginLeft: '6px', display: 'inline-block' }}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </h2>

            {/* FIX: Images/Powerpoint buttons now live here, right-aligned
                next to the single title, instead of a second duplicated
                title row further down the page. */}
            {processedSlides.length > 0 && (
              <div className="export-buttons-group">
                <button
                  className="download-all-png-button"
                  onClick={() => setShowPNGModal(true)}
                  disabled={!fontsLoaded}
                  title="Download selected slides as PNG"
                >
                  Images
                </button>
                <button
                  className="download-all-pptx-button"
                  onClick={handleDownloadAllPPTX}
                  disabled={!fontsLoaded || isExportingPPTX}
                  title="Download all slides as PowerPoint"
                >
                  {isExportingPPTX ? 'Exporting...' : 'Powerpoint'}
                </button>
              </div>
            )}
          </div>

        </div>

        {/* Video player — full width, no side panel anymore */}
        <div className="player-stack">
          {/* Duplicate title + buttons row removed — they now live in the
              prompt-top-section header above, right-aligned next to the
              single title. */}

          <div className="results-container">
            {showRightPanelLoading && (
              <LoadingState
                message={loadingInfo.message}
                submessage={loadingInfo.submessage}
              />
            )}

            {!showRightPanelLoading && processedSlides.length > 0 && (
              <div className="player-with-editor">
                {/* Left side — PowerPoint-style thumbnail rail. Shows every
                    slide as a real rendered mini-preview with its number;
                    clicking one jumps the player straight to that slide. */}
                <SlideThumbnailRail
                  processedSlides={processedSlides}
                  slideTemplates={slideTemplates}
                  getSlideImageUrl={getSlideImageUrl}
                  fontsLoaded={fontsLoaded}
                  currentSlideIndex={currentSlideIndex}
                  onSelectSlide={setCurrentSlideIndex}
                />

                {/* Right side — player, with the active slide's editable
                    text box sitting directly below the play controls. */}
                <div className="player-column">
                  <SlidePlayer
                    processedSlides={processedSlides}
                    slideTemplates={slideTemplates}
                    getSlideImageUrl={getSlideImageUrl}
                    fontsLoaded={fontsLoaded}
                    getCurrentTemplate={getCurrentTemplate}
                    getFilteredTemplatesForSlide={getFilteredTemplatesForSlide}
                    handleTemplatePrev={handleTemplatePrev}
                    handleTemplateNext={handleTemplateNext}
                    handleLayoutFilterChange={handleLayoutFilterChange}
                    getLayoutFilter={getLayoutFilter}
                    handleImageUpload={handleImageUpload}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    slideImages={slideImages}
                    currentSlideIndex={currentSlideIndex}
                    onCurrentSlideChange={setCurrentSlideIndex}
                  />

                  <div className="below-player-editor">
                    <CurrentSlideEditor
                      key={currentSlideIndex}
                      slide={processedSlides[currentSlideIndex]}
                      slideIndex={currentSlideIndex}
                      totalSlides={processedSlides.length}
                      isActive={true}
                      onCopyText={copySlideText}
                      onUpdateSlide={handleUpdateSlideContent}
                    />
                  </div>
                </div>
              </div>
            )}

            {!showRightPanelLoading && !processedSlides.length && (
              <div className="empty-state">
                <div className="empty-icon">📭</div>
                <h3>No results yet</h3>
                <p>
                  {uploadMode === 'prompt'
                    ? 'Your slides will appear here'
                    : 'Enter a prompt to get started'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Per-slide editable box now sits below the player's controls in
            the player column above — no more separate side panel and no
            more horizontal strip of every slide's card. */}

        {/* All Export Modals */}
        <AllExports
          showPNGModal={showPNGModal}
          setShowPNGModal={setShowPNGModal}
          processedSlides={processedSlides}
          selectedSlides={selectedSlides}
          handleSelectAllSlides={handleSelectAllSlides}
          handleToggleSlide={handleToggleSlide}
          handleExportPNG={handleExportPNG}
          isExportingPNG={isExportingPNG}
        />

        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          currentPlan={planTracker.userPlan || userPlan || 'free'}
          limitType={upgradeModalConfig.limitType}
          usage={planTracker.usage}
          suggestedPlan={upgradeModalConfig.suggestedPlan}
        />
      </div>
      </div>
    </div>
  );
}

export default SlideGenerator;